import React from 'react';
import { Navigate } from 'react-router-dom';
import { useSupabaseAuth } from '@/hooks/auth';
import { Loader2 } from 'lucide-react';

interface PasswordChangeGuardProps {
  children: React.ReactNode;
}

const PasswordChangeGuard: React.FC<PasswordChangeGuardProps> = ({ children }) => {
  const { requiresPasswordChange, session, isLoading, profile } = useSupabaseAuth();

  // Show loading state while auth is being determined
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Wait for profile to be loaded before checking password change requirement
  if (session && !profile) {
    console.log('⚠️ PasswordChangeGuard: Waiting for profile to load...');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // If user is logged in and requires password change, redirect to change password page
  // Safety check: Only redirect if we have a valid profile to prevent loops
  if (session && profile && requiresPasswordChange) {
    console.log('🔐 PasswordChangeGuard: Redirecting to /change-password', {
      userId: session.user.id,
      profileId: profile.id,
      requiresPasswordChange
    });
    return <Navigate to="/change-password" replace />;
  }

  console.log('✅ PasswordChangeGuard: Allowing access', {
    hasSession: !!session,
    hasProfile: !!profile,
    requiresPasswordChange
  });

  return <>{children}</>;
};

export default PasswordChangeGuard;
